import { useState } from 'react';

import { Button } from '../../button';

import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';
import {
  ANSWERERS,
  answererLabels,
  blockedCount,
  CACHE_REFINEMENT,
  CACHE_REPORTS,
  cacheReportLabels,
  cacheSteps,
  cacheSummary,
  cacheTransition,
  cachedShareText,
  cachedTotal,
  COST_QUESTIONS,
  COST_REFINEMENT,
  costCalls,
  costQuestionLabels,
  costSummary,
  costTokensText,
  costTransition,
  GUARDRAIL_REFINEMENT,
  guardrailSummary,
  guardrailTransition,
  HANDOFF_REFINEMENT,
  handoffRuns,
  handoffSummary,
  handoffTransition,
  initialCache,
  initialCost,
  initialGuardrail,
  initialHandoff,
  LAYOUTS,
  layoutLabels,
  outcomeText,
  passedCount,
  promptText,
  REPLIES,
  replyItems,
  replyLabels,
  sentTotal,
  STEP_COUNTS,
  stepCountLabels,
  toAnswerer,
  toCacheReport,
  toCostQuestion,
  tokensPerCall,
  toLayout,
  toReply,
  toStepCount,
  VENUE_QUESTION,
  whyText,
  wrongStepLands,
  type CacheAction,
  type CostAction,
  type GuardrailAction,
  type HandoffAction,
} from './multi-models';

import './relationship-labs.css';
import './multi-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const tokens = (value: number) => value.toLocaleString('en-US');
const REPLAY = 'This graphic replays real runs.';
const COMPUTED = 'This graphic computes from real totals.';
const CACHE_CAPTION =
  'This graphic computes from the 1,920 cached tokens of a real run, assuming each step after the first reads the cache.';

export function MultiHandoffLab() {
  const [state, setState] = useState(initialHandoff);
  const act = (action: HandoffAction) => setState((current) => handoffTransition(current, action));
  const run = handoffRuns[state.answerer];
  const summary = handoffSummary(state);
  return (
    <Lab
      title="What does the specialist cost against the generalist?"
      eyebrow="Several assistants · a handoff is a tool call"
      className="rel-lab multi-lab"
    >
      <Predict>
        What does the specialist cost against the generalist? Choose Who answers, then press Ask the
        venue question.
      </Predict>
      <div className="cb-controls">
        <label>
          Who answers
          <ModelSelect
            aria-label="Who answers"
            value={state.answerer}
            onValueChange={(value) => act({ type: 'answerer', value: toAnswerer(value) })}
            options={ANSWERERS.map((id) => ({ value: id, label: answererLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.asked} onClick={() => act({ type: 'ask' })}>
          Ask the venue question
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The question">
        <pre aria-label="The question">{VENUE_QUESTION}</pre>
      </Panel>
      <Panel label="In the prompt on every call">
        <pre aria-label="In the prompt on every call">{promptText(run)}</pre>
      </Panel>
      <Table
        label="The calls"
        columns={['Call', 'What happened']}
        template="minmax(0, 0.4fr) minmax(0, 3fr)"
        rows={state.asked ? run.calls.map((call, index) => [index + 1, call.what]) : []}
      />
      <Panel label="The answer">
        <pre aria-label="The answer">{state.asked ? run.answer : '(not asked yet)'}</pre>
      </Panel>
      {state.asked ? (
        <Facts
          values={[
            ['Calls', String(run.calls.length)],
            ['Prompt tokens', tokens(run.promptTokens)],
            ['Per call', `about ${tokens(tokensPerCall(run))}`],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.asked ? <p className="rel-caption">{HANDOFF_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function MultiCostLab() {
  const [state, setState] = useState(initialCost);
  const act = (action: CostAction) => setState((current) => costTransition(current, action));
  const calls = costCalls(state.question, state.layout);
  const summary = costSummary(state);
  return (
    <Lab
      title="When does a handoff pay?"
      eyebrow="Several assistants · the arithmetic of a handoff"
      className="rel-lab multi-lab"
    >
      <Predict>
        When does a handoff pay? Choose a Question and a Layout, then press Compute.
      </Predict>
      <div className="cb-controls">
        <label>
          Question
          <ModelSelect
            aria-label="Question"
            value={state.question}
            onValueChange={(value) => act({ type: 'question', value: toCostQuestion(value) })}
            options={COST_QUESTIONS.map((id) => ({ value: id, label: costQuestionLabels[id] }))}
          />
        </label>
        <label>
          Layout
          <ModelSelect
            aria-label="Layout"
            value={state.layout}
            onValueChange={(value) => act({ type: 'layout', value: toLayout(value) })}
            options={LAYOUTS.map((id) => ({ value: id, label: layoutLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.computed} onClick={() => act({ type: 'compute' })}>
          Compute
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="The calls"
        columns={['Call', 'Loop', 'What happened', 'Prompt tokens']}
        template="minmax(0, 0.4fr) minmax(0, 0.9fr) minmax(0, 2.2fr) minmax(0, 0.9fr)"
        rows={
          state.computed
            ? calls.map((call, index) => [index + 1, call.loop, call.what, tokens(call.tokens)])
            : []
        }
      />
      {state.computed ? (
        <Facts
          values={[
            ['Calls', String(calls.length)],
            ['Prompt tokens', costTokensText(state.question, state.layout)],
            ['A wrong first step lands at', wrongStepLands(calls)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.computed ? <p className="rel-caption">{COST_REFINEMENT}</p> : null}
      <p className="rel-caption">{COMPUTED}</p>
    </Lab>
  );
}

export function MultiGuardrailLab() {
  const [state, setState] = useState(initialGuardrail);
  const act = (action: GuardrailAction) =>
    setState((current) => guardrailTransition(current, action));
  const items = replyItems[state.reply];
  const summary = guardrailSummary(state);
  return (
    <Lab
      title="What leaves the assistant, and what stops it?"
      eyebrow="Several assistants · guardrails and sandboxes"
      className="rel-lab multi-lab"
    >
      <Predict>
        What leaves the assistant, and what stops it? Choose a Reply and Guardrail on, then press
        Send.
      </Predict>
      <div className="cb-controls">
        <label>
          Reply
          <ModelSelect
            aria-label="Reply"
            value={state.reply}
            onValueChange={(value) => act({ type: 'reply', value: toReply(value) })}
            options={REPLIES.map((id) => ({ value: id, label: replyLabels[id] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.guardrail}
          onClick={() => act({ type: 'toggle-guardrail' })}
        >
          Guardrail on
        </Button>
        <Button size="sm" disabled={state.sent} onClick={() => act({ type: 'send' })}>
          Send
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="What leaves"
        columns={['Item', 'Outcome']}
        template="minmax(0, 1.4fr) minmax(0, 1.6fr)"
        rows={
          state.sent ? items.map((entry) => [entry.what, outcomeText(entry, state.guardrail)]) : []
        }
      />
      {state.sent ? (
        <Facts
          values={[
            ['Passed', `${passedCount(state.reply, state.guardrail)} of ${items.length}`],
            ['Blocked', String(blockedCount(state.reply, state.guardrail))],
            ['Why', whyText(state.reply, state.guardrail)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.sent ? <p className="rel-caption">{GUARDRAIL_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function MultiCacheLab() {
  const [state, setState] = useState(initialCache);
  const act = (action: CacheAction) => setState((current) => cacheTransition(current, action));
  const steps = cacheSteps(state.steps, state.report);
  const summary = cacheSummary(state);
  return (
    <Lab
      title="What does caching the tool block save on a six-step turn?"
      eyebrow="Several assistants · the same prefix on every step"
      className="rel-lab multi-lab"
    >
      <Predict>
        What does caching the tool block save on a six-step turn? Choose Steps and Cached tokens
        reported, then press Compute.
      </Predict>
      <div className="cb-controls">
        <label>
          Steps
          <ModelSelect
            aria-label="Steps"
            value={String(state.steps)}
            onValueChange={(value) => act({ type: 'steps', value: toStepCount(value) })}
            options={STEP_COUNTS.map((count) => ({
              value: String(count),
              label: stepCountLabels[count],
            }))}
          />
        </label>
        <label>
          Cached tokens reported
          <ModelSelect
            aria-label="Cached tokens reported"
            value={state.report}
            onValueChange={(value) => act({ type: 'report', value: toCacheReport(value) })}
            options={CACHE_REPORTS.map((id) => ({ value: id, label: cacheReportLabels[id] }))}
          />
        </label>
        <Button size="sm" disabled={state.computed} onClick={() => act({ type: 'compute' })}>
          Compute
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Table
        label="The turn"
        columns={['Step', 'Prompt tokens sent', 'Cached tokens reported']}
        template="minmax(0, 0.5fr) minmax(0, 1.2fr) minmax(0, 1.4fr)"
        rows={
          state.computed
            ? steps.map((step) => [step.step, tokens(step.sent), tokens(step.cached)])
            : []
        }
      />
      {state.computed ? (
        <Facts
          values={[
            ['Prompt tokens sent', tokens(sentTotal(steps))],
            ['Cached tokens reported', tokens(cachedTotal(steps))],
            ['Share read from the cache', cachedShareText(steps)],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.computed ? <p className="rel-caption">{CACHE_REFINEMENT}</p> : null}
      <p className="rel-caption">{CACHE_CAPTION}</p>
    </Lab>
  );
}
